import { FormState } from '../types';

// Calculate salary increase percentage
/**
 * Calculates the percentage increase between the inputted salary and the selected salary card.
 *
 * @param inputtedSalary - The original salary value provided by the user
 * @param selectedSalaryCard - The salary value from the selected card for comparison
 * @returns The percentage difference between the two salaries. Returns 0 if either salary is not provided.
 */
export const calculateIncreasePercentage = (
  inputtedSalary: number,
  selectedSalaryCard: number,
) => {
  // If either salary is not provided, return 0
  if (!inputtedSalary || !selectedSalaryCard) return 0;

  return ((selectedSalaryCard - inputtedSalary) / inputtedSalary) * 100;
};

// Calculate salary increase amount
/**
 * Calculates the difference between the selected salary and the inputted salary.
 *
 * @param inputtedSalary - The current salary input by the user
 * @param selectedSalaryCard - The target salary selected by the user
 * @returns The difference between the selected salary and inputted salary, or 0 if either value is falsy
 */
export const calculateIncreaseAmount = (
  inputtedSalary: number,
  selectedSalaryCard: number,
) => {
  if (!inputtedSalary || !selectedSalaryCard) return 0;

  return selectedSalaryCard - inputtedSalary;
};

// Helper to get numeric IDs from slugs
/**
 * Retrieves the numeric ID of an option from a form's options based on the given field and slug.
 * @param field - The field name to look for in the options object.
 * @param slug - The slug identifier to match against options in the specified field.
 * @param options - The options object from the form state containing available selections.
 * @returns The numeric ID of the found option, or 0 if the field doesn't exist or the slug wasn't found.
 */
export const getNumericId = (
  field: string,
  slug: string,
  options: FormState['options'],
) => {
  if (!options[field as keyof typeof options]) return 0;

  const option = options[field as keyof typeof options]?.find(
    (opt) => opt.slug === slug,
  );
  return option?.id || 0;
};
